/**
 * 封装 axios
 */
import axios from 'axios'
import { ElMessage } from 'element-plus'

import store from '@/store'
import router from '@/router'
import Config from '@/config'
import autoJump from '@/lin/util/auto-jump'
import ErrorCode from '@/config/error-code'
import { getToken, saveAccessToken } from '@/lin/util/token'

const config = {
  baseURL: Config.baseURL || '',
  timeout: 30 * 1000, // 请求超时时间设置
  crossDomain: true,
  // withCredentials: true, // Check cross-site Access-Control
  // 定义可获得的http响应状态码
  // return true、设置为null或者undefined，promise将resolved,否则将rejected
  validateStatus(status) {
    return status >= 200 && status < 510
  },
}

/**
 * 错误码是否是refresh相关
 * @param { number } code 错误码
 */
function refreshTokenException(code) {
  const codes = [10000, 10042, 10050, 10052, 10012]
  return codes.includes(code)
}

function createHttpError(res, fallbackMessage = '请求失败') {
  const backendMessage = res?.data?.message
  const message = typeof backendMessage === 'string' ? backendMessage : fallbackMessage
  const error = new Error(message)
  error.response = res
  error.code = res?.data?.code
  return error
}

// 创建请求实例
const _axios = axios.create(config)

_axios.interceptors.request.use(
  originConfig => {
    // 有 API 请求重新计时
    autoJump(router)

    const reqConfig = { ...originConfig }

    // step1: 容错处理
    if (!reqConfig.url) {
      console.error('request need url')
    }

    reqConfig.method = reqConfig.method.toLowerCase() // 大小写容错

    // 参数容错
    if (reqConfig.method === 'get') {
      if (!reqConfig.params) {
        reqConfig.params = reqConfig.data || {}
      }
    } else if (reqConfig.method === 'post') {
      if (!reqConfig.data) {
        reqConfig.data = reqConfig.params || {}
      }

      // 检测是否包含文件类型, 若包含则进行 formData 封装
      let hasFile = false
      Object.keys(reqConfig.data).forEach(key => {
        if (typeof reqConfig.data[key] === 'object') {
          const item = reqConfig.data[key]
          if (item instanceof FileList || item instanceof File || item instanceof Blob) {
            hasFile = true
          }
        }
      })

      // 检测到存在文件使用 FormData 提交数据
      if (hasFile) {
        const formData = new FormData()
        Object.keys(reqConfig.data).forEach(key => {
          formData.append(key, reqConfig.data[key])
        })
        reqConfig.data = formData
      }
    }

    // step2: permission 处理
    if (reqConfig.url === 'cms/user/refresh') {
      const refreshToken = getToken('refresh_token')
      if (refreshToken) {
        reqConfig.headers.Authorization = refreshToken
      }
    } else {
      const accessToken = getToken('access_token')
      if (accessToken) {
        reqConfig.headers.Authorization = accessToken
      }
    }

    return reqConfig
  },
  error => Promise.reject(error),
)

// Add a response interceptor
// Add a response interceptor
_axios.interceptors.response.use(
  async res => {
    if (res.status.toString().charAt(0) === '2') {
      // 如果是 blob 下载，直接返回整个 res（拿到 headers，且 res.data 是 Blob）
      if (res.config?.responseType === 'blob') {
        return res
      }
      return res.data
    }

    const { code, message } = res.data
    let tipMessage = ''
    const { url } = res.config

    // refresh_token 异常，直接登出
    if (refreshTokenException(code)) {
      setTimeout(() => {
        store.dispatch('loginOut')
        const { origin } = window.location
        window.location.href = origin
      }, 1500)
      return null
    }

    // accessToken 相关，刷新令牌并重放请求
    if (code === 10041 || code === 10051) {
      // 你原代码的 cache 每次都会重置，这里先保持最小改动，只解决 lint
      // 若你想做真正的“单飞”(single-flight)刷新，我也可以再给你改一版
      const refreshResult = await _axios('cms/user/refresh')
      saveAccessToken(refreshResult.access_token)
      const result = await _axios(res.config)
      return result
    }

    // 特殊情况：如果本次请求添加了 handleError: true，用户自行通过 try/catch 处理
    if (res.config.handleError) {
      throw createHttpError(res)
    }

    // 前端自定义错误信息
    if (Config.useFrontEndErrorMsg && !res.config.showBackend) {
      const errorArr = Object.entries(ErrorCode).filter(v => v[0] === code.toString())
      if (errorArr.length > 0 && errorArr[0][1] !== '') {
        ;[[, tipMessage]] = errorArr
      } else {
        tipMessage = ErrorCode['777']
      }
    }

    // 后端 message 覆盖
    if (typeof message === 'string') {
      tipMessage = message
    }
    if (Object.prototype.toString.call(message) === '[object Object]') {
      ;[tipMessage] = Object.values(message).flat()
    }
    if (Object.prototype.toString.call(message) === '[object Array]') {
      ;[tipMessage] = message
    }

    tipMessage = tipMessage || '请求失败'
    ElMessage.error(tipMessage)
    throw createHttpError(res, tipMessage)
  },
  error => {
    if (!error.response) {
      ElMessage.error('请检查 API 是否异常')
      console.log('error', error)
    }
    if (error.code === 'ECONNABORTED' && error.message.indexOf('timeout') !== -1) {
      ElMessage.warning('请求超时')
    }
    return Promise.reject(error)
  },
)

// 导出常用函数

/**
 * @param {string} url
 * @param {object} data
 * @param {object} params
 */
export function post(url, data = {}, params = {}) {
  return _axios({
    method: 'post',
    url,
    data,
    params,
  })
}

/**
 * @param {string} url
 * @param {object} params
 */
export function get(url, params = {}) {
  return _axios({
    method: 'get',
    url,
    params,
  })
}

/**
 * @param {string} url
 * @param {object} data
 * @param {object} params
 */
export function put(url, data = {}, params = {}) {
  return _axios({
    method: 'put',
    url,
    params,
    data,
  })
}

/**
 * @param {string} url
 * @param {object} params
 */
export function _delete(url, params = {}) {
  return _axios({
    method: 'delete',
    url,
    params,
  })
}

/**
 * 下载/导出：GET Blob
 * @param {string} url
 * @param {object} params
 * @param {object} axiosConfig 额外 axios 配置，如 headers、timeout
 */
export function getBlob(url, params = {}, axiosConfig = {}) {
  return _axios({
    method: 'get',
    url,
    params,
    responseType: 'blob',
    ...axiosConfig,
  })
}

export default _axios
